import React, { useState, useMemo } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Divider,
  Chip,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Search,
  Timeline,
  NetworkCheck,
  Settings,
  SignalCellularAlt,
  Logout,
  Mic,
  Group as GroupIcon,
  AdminPanelSettings,
  KeyboardArrowDown,
  Analytics,
  Description,
  Flag as FlagIcon,
  Security as SecurityIcon,
  DarkMode,
  LightMode,
  Folder as CaseIcon,
  Psychology as AIIcon,
  AccountTree as WorkflowIcon,
  Policy as InvestigationIcon,
  Shield as ShieldIcon,
  Sms as SmsIcon,
  Phone as PhoneNavIcon,
  MyLocation as LocateNavIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { useThemeMode } from '../context/ThemeContext';
import SessionIndicator from './SessionIndicator';
import InvestigationAssistant from './InvestigationAssistant';
import formatCount from '../utils/numberFormat';

const drawerWidth = 260;

// Streamlined menu items - consolidated navigation
const baseMenuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', description: 'Overview & Stats' },
  { text: 'CDR Search', icon: <Search />, path: '/cdr-search', description: 'Search All Carriers' },
  { text: 'AI Assistant', icon: <AIIcon />, path: '/ai-chat', description: 'CDR Intelligence Chat' },
  { text: 'Real-Time Monitor', icon: <Timeline />, path: '/realtime', description: 'Live Activity' },
  { text: 'Live Locate', icon: <LocateNavIcon />, path: '/live-locate', description: 'Track Phone Location' },
  { text: 'Network Analysis', icon: <NetworkCheck />, path: '/network', description: 'Network Topology' },
  { text: 'Recordings', icon: <Mic />, path: '/recordings', description: 'Audio Recordings' },
  { text: 'Analytics', icon: <Analytics />, path: '/analytics', description: 'Intelligence & Insights' },
  { text: 'Cases', icon: <CaseIcon />, path: '/cases', description: 'Investigation Cases' },
  { text: 'Investigation Hub', icon: <InvestigationIcon />, path: '/investigation', description: 'AI-Powered Analysis' },
  { text: 'Reports', icon: <Description />, path: '/reports', description: 'Generate Reports' },
  { text: "Rwanda Patterns", icon: <FlagIcon />, path: "/rwanda-patterns", description: "Rwanda Call Analysis" },
  { text: "Bypass Detection", icon: <SecurityIcon />, path: "/bypass-detection", description: "Fraud & Bypass Scanner" },
  { text: "Threat Pyramid", icon: <ShieldIcon />, path: "/threat-pyramid", description: "Security Threat Intelligence" },
  { text: "SMS Messaging", icon: <SmsIcon />, path: "/sms", description: "Send SMS Messages" },
  { text: "WebPhone", icon: <PhoneNavIcon />, path: "/phone", description: "WebRTC Softphone" },
];

const adminMenuItems = [
  { text: 'Workflows', icon: <WorkflowIcon />, path: '/workflows', adminOnly: true, description: 'Automation & Sync' },
  { text: 'User Management', icon: <GroupIcon />, path: '/users', adminOnly: true, description: 'Manage Users' },
  { text: 'Audit Trail', icon: <SecurityIcon />, path: '/audit', adminOnly: true, description: 'Activity Logs' },
];

const bottomMenuItems = [
  { text: 'Settings', icon: <Settings />, path: '/settings', description: 'App Settings' },
];

function Layout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { stats: contextStats, globalSummary } = useApp();
  const { user, logout, isAdmin } = useAuth();
  const { isConnected, realtimeMetrics } = useWebSocket();
  const { mode, toggleTheme, isDark } = useThemeMode();

  const menuItems = useMemo(() => {
    const items = [...baseMenuItems];
    if (isAdmin) {
      items.push(...adminMenuItems);
    }
    items.push(...bottomMenuItems);
    return items;
  }, [isAdmin]);

  const aggregatedStats = useMemo(() => {
    if (realtimeMetrics) return realtimeMetrics;
    if (globalSummary && typeof globalSummary === 'object') {
      const total = Number(globalSummary.total_records || globalSummary.totalCdrs || globalSummary.total_cdrs || globalSummary.total);
      if (!Number.isNaN(total) && Number.isFinite(total)) {
        return { totalRecords: total };
      }
    }
    if (contextStats && typeof contextStats === 'object') {
      const operatorTotals = Object.values(contextStats)
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry) => Number(entry.total_records || entry.totalRecords || entry.totalCdrs || 0))
        .filter((val) => !Number.isNaN(val) && Number.isFinite(val));
      if (operatorTotals.length) {
        return { totalRecords: operatorTotals.reduce((sum, val) => sum + val, 0) };
      }
    }
    return null;
  }, [realtimeMetrics, contextStats, globalSummary]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'analyst': return 'primary';
      default: return 'default';
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
      {/* Logo */}
      <Toolbar sx={{ backgroundColor: 'background.paper', py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1565C0, #0D47A1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(21, 101, 192, 0.3)',
            }}
          >
            <SignalCellularAlt sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
              CDR Hub
            </Typography>
            <Typography variant="caption" sx={{ color: '#1565C0', fontSize: '0.7rem', fontWeight: 500 }}>
              Intelligence Platform
            </Typography>
          </Box>
        </Box>
      </Toolbar>

      {/* Stats Badge */}
      {aggregatedStats?.totalRecords && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(21, 101, 192, 0.06)', mx: 1.5, mt: 1.5, borderRadius: 2, border: '1px solid rgba(21, 101, 192, 0.1)' }}>
          <Typography variant="caption" sx={{ color: '#1565C0', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 600 }}>
            Total Records
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
            {formatCount(aggregatedStats.totalRecords)}
          </Typography>
        </Box>
      )}

      {/* Main Navigation */}
      <List sx={{ flex: 1, py: 2, px: 1 }}>
        {menuItems.map((item, index) => {
          const isActive = location.pathname === item.path;
          const showDivider = item.adminOnly && menuItems[index - 1] && !menuItems[index - 1].adminOnly;

          return (
            <React.Fragment key={item.text}>
              {showDivider && (
                <Divider sx={{ my: 1.5, mx: 1 }}>
                  <Chip
                    label="Admin"
                    size="small"
                    icon={<AdminPanelSettings sx={{ fontSize: 12, color: '#D32F2F' }} />}
                    sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(211, 47, 47, 0.08)', color: '#D32F2F' }}
                  />
                </Divider>
              )}
              {item.text === 'Settings' && <Divider sx={{ my: 1.5, mx: 1 }} />}
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: 1.25,
                    bgcolor: isActive ? 'rgba(21, 101, 192, 0.08)' : 'transparent',
                    borderLeft: isActive ? '3px solid #1565C0' : '3px solid transparent',
                    '&:hover': {
                      bgcolor: isActive ? 'rgba(21, 101, 192, 0.12)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: isActive ? '#1565C0' : '#546E7A' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? 'primary.main' : 'text.primary',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>

      {/* User Section */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            cursor: 'pointer',
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.selected', borderColor: 'primary.light' },
          }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
            {user?.username?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' }} noWrap>
              {user?.username || 'User'}
            </Typography>
            <Chip
              label={user?.role || 'Viewer'}
              size="small"
              color={getRoleColor(user?.role)}
              sx={{ height: 18, fontSize: '0.6rem' }}
            />
          </Box>
          <KeyboardArrowDown sx={{ color: 'text.secondary', fontSize: 18 }} />
        </Box>
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        PaperProps={{ sx: { width: 200, mt: 1 } }}
      >
        <MenuItem onClick={() => { navigate('/settings'); setUserMenuAnchor(null); }}>
          <Settings sx={{ mr: 1.5, fontSize: 18, color: '#546E7A' }} /> Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: '#D32F2F' }}>
          <Logout sx={{ mr: 1.5, fontSize: 18 }} /> Logout
        </MenuItem>
      </Menu>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile App Bar */}
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 600, color: 'text.primary' }}>CDR Hub</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={toggleTheme} size="small" sx={{ mr: 1 }}>
            {isDark ? <LightMode sx={{ fontSize: 20, color: '#FFC107' }} /> : <DarkMode sx={{ fontSize: 20, color: '#546E7A' }} />}
          </IconButton>
          {isConnected && (
            <Chip
              size="small"
              label="Live"
              sx={{ bgcolor: 'rgba(46,125,50,0.1)', color: '#2E7D32', height: 24 }}
            />
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: { xs: 8, md: 0 },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        {/* Connection Status & Theme Toggle */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', mb: 2, gap: 2, alignItems: 'center' }}>
          {isConnected && (
            <Chip
              size="small"
              icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2E7D32', mr: 0.5 }} />}
              label="Connected"
              sx={{ bgcolor: 'rgba(46,125,50,0.1)', color: '#2E7D32' }}
            />
          )}
          <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            <IconButton
              onClick={toggleTheme}
              size="small"
              sx={{
                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                },
              }}
            >
              {isDark ? <LightMode sx={{ fontSize: 20, color: '#FFC107' }} /> : <DarkMode sx={{ fontSize: 20, color: '#546E7A' }} />}
            </IconButton>
          </Tooltip>
          <SessionIndicator />
        </Box>
        {children}

        {/* AI Investigation Assistant - available on all pages */}
        <InvestigationAssistant userId={user?.id} sessionId={user?.username} />
      </Box>
    </Box>
  );
}

export default Layout;
